import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isEnPath, localize, stripEn } from "@/lib/locale";

export const SITE_URL = "https://oceanconnect.co.kr";
// 검색 결과 제목 뒤에 붙는 회사명. 검색 결과에는 제목 앞부분 30자 안팎만 보이므로
// 모든 페이지에서 "검색어가 담긴 페이지 제목 | 회사명" 순서로 둡니다(홈 포함).
const BRAND = { ko: "오션커넥트 OCEAN CONNECT", en: "OCEAN CONNECT" };
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
// 색인 허용 + 검색 결과 · Discover 에 큰 미리보기 이미지 허용 (scripts/prerender.mjs · verify-build.mjs 와 같은 값)
const ROBOTS_INDEX = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

type Seo = {
  title: string; // 페이지 제목 (사이트명은 자동으로 뒤에 붙음)
  description: string;
  image?: string;
  noindex?: boolean;
  /** 공유 미리보기 종류: 기술자료 · 소식은 article (SNS · 검색엔진이 기사로 인식) */
  type?: "website" | "article";
  /** article 일 때 게시일 (YYYY-MM-DD) */
  published?: string;
};

export type SsrSeo = {
  title: string;
  description: string;
  url: string;
  image: string;
  noindex: boolean;
  lang: "ko" | "en";
  /** 같은 페이지의 언어별 주소 (hreflang) */
  alternates: { ko: string; en: string };
  type: "website" | "article";
  published?: string;
};

// 사전 렌더링(prerender) 중에는 document 가 없으므로 계산된 값을 여기에 모아 두고,
// entry-server 가 렌더링 직후 takeSsrSeo() 로 가져가 <head> 에 써 넣습니다.
let ssrSeo: SsrSeo | null = null;
export function takeSsrSeo() {
  const s = ssrSeo;
  ssrSeo = null;
  return s;
}

function setMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}

const abs = (path: string) => `${SITE_URL}${path === "/" ? "/" : path.replace(/\/$/, "")}`;

// 페이지별 제목 · 설명 · 정식 주소 · 언어별 주소 · 공유 미리보기를 <head> 에 반영합니다.
// 정적 사이트(SPA)라 라우트가 바뀔 때마다 직접 갱신해야 검색엔진과 SNS 가 페이지를 구분합니다.
export function useSeo({ title, description, image, noindex, type = "website", published }: Seo) {
  const { pathname } = useLocation();
  const lang = isEnPath(pathname) ? "en" : "ko";

  const fullTitle = `${title} | ${BRAND[lang]}`;
  const url = abs(pathname);
  const base = stripEn(pathname);
  const alternates = { ko: abs(localize(base, false)), en: abs(localize(base, true)) };
  const img = image || DEFAULT_IMAGE;

  if (typeof document === "undefined") {
    ssrSeo = { title: fullTitle, description, url, image: img, noindex: !!noindex, lang, alternates, type, published };
  }

  // 공유 미리보기 종류 · 게시일 (다른 페이지로 이동하면 기사 정보는 지움)
  useEffect(() => {
    setMeta('meta[property="og:type"]', { property: "og:type", content: type });
    const pub = document.head.querySelector('meta[property="article:published_time"]');
    if (type === "article" && published) setMeta('meta[property="article:published_time"]', { property: "article:published_time", content: published });
    else if (pub) pub.remove();
  }, [type, published]);

  useEffect(() => {
    document.title = fullTitle;
    document.documentElement.lang = lang;
    setMeta('meta[name="description"]', { name: "description", content: description });
    setMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex, nofollow" : ROBOTS_INDEX });
    setMeta('link[rel="canonical"]', { rel: "canonical", href: url });
    setMeta('link[rel="alternate"][hreflang="ko"]', { rel: "alternate", hreflang: "ko", href: alternates.ko });
    setMeta('link[rel="alternate"][hreflang="en"]', { rel: "alternate", hreflang: "en", href: alternates.en });
    setMeta('link[rel="alternate"][hreflang="x-default"]', { rel: "alternate", hreflang: "x-default", href: alternates.ko });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: lang === "en" ? "en_US" : "ko_KR" });
    setMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: url });
    setMeta('meta[property="og:image"]', { property: "og:image", content: img });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: img });
  }, [fullTitle, description, url, img, noindex, lang, alternates.ko, alternates.en]);
}
